/**
 * Content column shared by the desktop sidebar (FilterSidebar) and the mobile
 * sheet (FilterSheet). Renders every filter section top-to-bottom and the
 * "clear all" link.
 *
 * Extracted from `app/(main)/products/ProductsClient.tsx` lines 1333–1453.
 */

"use client";

import {
  CategorySection,
  type CategoryTreeNode,
} from "../FilterSections/CategorySection";
import { ColourSection } from "../FilterSections/ColourSection";
import { FlowerSection } from "../FilterSections/FlowerSection";
import {
  DeliveryTimingSection,
  type DeliveryTiming,
} from "../FilterSections/DeliveryTimingSection";
import { OccasionSection } from "../FilterSections/OccasionSection";
import { PriceRangeSection } from "../FilterSections/PriceRangeSection";
import {
  SortSection,
  type ProductSort,
} from "../FilterSections/SortSection";

export interface FilterSidebarBodyProps {
  /** Hierarchical category tree. Primary data source for the filter. */
  categoryTree: CategoryTreeNode[];
  /** Flat category names — fallback while the tree loads / if the API fails. */
  categories: string[];
  colours: string[];
  /** Flower/plant names present on real products (`listFlowerFacets`). */
  flowers?: string[];
  sort: ProductSort;
  onSortChange: (s: ProductSort) => void;
  categoriesSelected: string[];
  onToggleCategory: (c: string) => void;
  /** Replace the entire category selection — used by the tree parent checkbox. */
  onReplaceCategorySelection: (next: string[]) => void;
  deliveryTiming: DeliveryTiming;
  onDeliveryTimingChange: (v: DeliveryTiming) => void;
  occasionsSelected: string[];
  onToggleOccasion: (o: string) => void;
  coloursSelected: string[];
  onToggleColour: (c: string) => void;
  flowersSelected?: string[];
  onToggleFlower?: (f: string) => void;
  minPrice: string;
  maxPrice: string;
  onPriceChange: (min: string, max: string) => void;
  priceRangePence: { min: number; max: number } | null;
  openSections: Set<string>;
  onToggleSection: (title: string) => void;
  hasActiveFilters: boolean;
  onClearAllFilters: () => void;
  /**
   * When true, suppress the top-level category filter. Used on a specific
   * collection page (`/products/<slug>`) where the user is already in one
   * collection — offering a "switch collection" checkbox list inside the
   * filter would contradict the collection-first navigation model.
   */
  hideCategoryFilter?: boolean;
}

export function FilterSidebarBody({
  categoryTree,
  categories,
  colours,
  flowers = [],
  sort,
  onSortChange,
  categoriesSelected,
  onToggleCategory,
  onReplaceCategorySelection,
  deliveryTiming,
  onDeliveryTimingChange,
  occasionsSelected,
  onToggleOccasion,
  coloursSelected,
  onToggleColour,
  flowersSelected = [],
  onToggleFlower,
  minPrice,
  maxPrice,
  onPriceChange,
  priceRangePence,
  openSections,
  onToggleSection,
  hasActiveFilters,
  onClearAllFilters,
  hideCategoryFilter = false,
}: FilterSidebarBodyProps) {
  return (
    <div className="space-y-0">
      <SortSection
        value={sort}
        onChange={onSortChange}
        isOpen={openSections.has("Sort by")}
        onToggleSection={onToggleSection}
      />
      {hasActiveFilters && (
        <div className="border-b border-border py-2">
          <button
            type="button"
            onClick={onClearAllFilters}
            className="text-left text-sm font-medium text-primary underline underline-offset-2 hover:text-primary/80"
          >
            Clear all filters
          </button>
        </div>
      )}
      {!hideCategoryFilter && (
        <CategorySection
          tree={categoryTree}
          fallbackNames={categories}
          selected={categoriesSelected}
          onToggle={onToggleCategory}
          onReplaceSelection={onReplaceCategorySelection}
          isOpen={openSections.has("Product type")}
          onToggleSection={onToggleSection}
        />
      )}
      <DeliveryTimingSection
        value={deliveryTiming}
        onChange={onDeliveryTimingChange}
        isOpen={openSections.has("How to order")}
        onToggleSection={onToggleSection}
      />
      <OccasionSection
        selected={occasionsSelected}
        onToggle={onToggleOccasion}
        isOpen={openSections.has("Occasion")}
        onToggleSection={onToggleSection}
      />
      <ColourSection
        colours={colours}
        selected={coloursSelected}
        onToggle={onToggleColour}
        isOpen={openSections.has("Colour")}
        onToggleSection={onToggleSection}
      />
      {onToggleFlower && (
        <FlowerSection
          flowers={flowers}
          selected={flowersSelected}
          onToggle={onToggleFlower}
          isOpen={openSections.has("Flower")}
          onToggleSection={onToggleSection}
        />
      )}
      <PriceRangeSection
        minPrice={minPrice}
        maxPrice={maxPrice}
        onPriceChange={onPriceChange}
        priceRangePence={priceRangePence}
        isOpen={openSections.has("Price range (£)")}
        onToggleSection={onToggleSection}
      />
    </div>
  );
}
